/**
 * Role Testing Floating Action Button (FAB)
 *
 * Appears when admin role testing is active. Provides quick access to switch roles.
 * Uses standard MUI components and theme colors.
 */
import BadgeIcon from "@mui/icons-material/Badge";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Fab,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useActiveFBOs } from "../containers/ActiveFBOContainer";
import { useRoleOverride, OverridableRole } from "../stores/useRoleOverride";
import { ROLE_OPTIONS, formatRoleName } from "../utils/permissions";

export const RoleTestingFab: React.FC = () => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();
  const { isActive, overriddenRole, startRoleOverride, stopRoleOverride } = useRoleOverride();
  const [expanded, setExpanded] = React.useState(false);
  const [selectedRole, setSelectedRole] = React.useState<OverridableRole>(overriddenRole);

  // Sync selectedRole with store
  React.useEffect(() => {
    if (overriddenRole) {
      setSelectedRole(overriddenRole);
    }
  }, [overriddenRole]);

  // Don't render if override is not active
  if (!isActive) {
    return null;
  }

  const handleRoleChange = (newRole: OverridableRole) => {
    setSelectedRole(newRole);
    if (activeFBO?.id) {
      startRoleOverride(newRole, activeFBO.id);
    }
  };

  const handleExit = () => {
    stopRoleOverride();
    setExpanded(false);
  };

  return (
    <>
      {expanded && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: (theme) => theme.spacing(10),
            right: (theme) => theme.spacing(2),
            p: 2,
            zIndex: 1300,
            minWidth: 280,
          }}
        >
          <Stack direction="column" spacing={2}>
            <Typography variant="subtitle1" fontWeight="bold">
              {t('widgets.roleTesting.title')}
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel>{t('widgets.roleTesting.testingAsLabel')}</InputLabel>
              <Select
                value={selectedRole}
                onChange={(e) => handleRoleChange(e.target.value as OverridableRole)}
                label={t('widgets.roleTesting.testingAsLabel')}
              >
                {ROLE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="secondary"
              startIcon={<CloseIcon />}
              onClick={handleExit}
              fullWidth
            >
              {t('widgets.roleTesting.exitButton')}
            </Button>
          </Stack>
        </Paper>
      )}

      <Tooltip
        title={
          expanded
            ? t('widgets.roleTesting.close')
            : t('widgets.roleTesting.tooltipFormat', { role: formatRoleName(overriddenRole) })
        }
      >
        <Fab
          color="secondary"
          onClick={() => setExpanded(!expanded)}
          sx={{
            position: "fixed",
            bottom: (theme) => theme.spacing(2),
            right: (theme) => theme.spacing(2),
          }}
        >
          {expanded ? <CloseIcon /> : <BadgeIcon />}
        </Fab>
      </Tooltip>
    </>
  );
};
